import { useState } from 'react';
import { ExpandMore, ListAlt, ShoppingCart } from '@mui/icons-material';
import { useSelector } from 'react-redux';

import { nfRp } from '@/constants/nf';
import {
	EMPTY_CART_ITEMS,
	linesForChildAndDate,
	mealDateIso,
} from '@/helper/cartFamily';
import { paidDishLinesFromServingTimes } from '@/helper/dashboardStatus';
import useParentDashboardChild from '@/hooks/useParentDashboardChild';

export default function MenuChildOrdersAccordion() {
	const [paidOpen, setPaidOpen] = useState(false);
	const [cartOpen, setCartOpen] = useState(false);
	const selectedCustomer = useSelector((state) => state.auth.selectedCustomer);
	const orderDate = useSelector((state) => state.cart.orderDate);
	const cartItems = useSelector((state) => state.cart.items) ?? EMPTY_CART_ITEMS;
	const dateIso = mealDateIso(orderDate);

	const { childRow } = useParentDashboardChild(orderDate);
	const childId = selectedCustomer?.id;
	const fullName = selectedCustomer?.member?.name || 'Child';
	const nameParts = fullName.trim().split(/\s+/);
	const childName =
		nameParts.length > 1 && nameParts[0] === nameParts[0].toUpperCase()
			? nameParts[1]
			: nameParts[0];

	const unpaidLines = childId != null ? linesForChildAndDate(cartItems, childId, dateIso) : [];
	const paidLines = paidDishLinesFromServingTimes(childRow?.serving_times);
	const cartQty = unpaidLines.reduce((n, l) => n + (Number(l.quantity) || 0), 0);

	if (!cartQty && !paidLines.length) return null;

	return (
		<section className='order-history-context' aria-label='Orders for selected child and date'>
			{paidLines.length > 0 && (
				<div className='context-disclosure'>
					<button
						type='button'
						className='quick-trigger'
						aria-expanded={paidOpen}
						onClick={() => setPaidOpen((v) => !v)}
					>
						<ListAlt fontSize='small' />
						<span>View {childName}&apos;s existing orders</span>
						<ExpandMore
							className={paidOpen ? 'rotate-180' : undefined}
							fontSize='small'
						/>
					</button>
					{paidOpen && (
						<ul className='quick-view'>
							{paidLines.map((line) => (
								<li key={line.key} className='row' style={{ marginBottom: 6 }}>
									<span>
										{line.quantity} × {line.name}
									</span>
									<strong>{nfRp(line.lineTotal)}</strong>
								</li>
							))}
						</ul>
					)}
				</div>
			)}
			{cartQty > 0 && (
				<div className='context-disclosure'>
					<button
						type='button'
						className='quick-trigger cart-quick-trigger'
						aria-expanded={cartOpen}
						onClick={() => setCartOpen((v) => !v)}
					>
						<ShoppingCart fontSize='small' />
						<span>
							View {childName}&apos;s meals in cart ({cartQty})
						</span>
						<ExpandMore
							className={cartOpen ? 'rotate-180' : undefined}
							fontSize='small'
						/>
					</button>
					{cartOpen && (
						<ul className='quick-view'>
							{unpaidLines.map((line) => {
								const total =
									((+line.selling_price || +line.unit_price || +line.price || 0) +
										(line.add_ons || []).reduce(
											(a, b) => a + (+b.add_on?.price || +b?.price || 0),
											0
										)) *
									(Number(line.quantity) || 0);
								return (
									<li key={line.uid} className='row' style={{ marginBottom: 6 }}>
										<span>
											{line.quantity} × {line.name || line.product_name || 'Item'}
										</span>
										<strong>{nfRp(total)}</strong>
									</li>
								);
							})}
						</ul>
					)}
				</div>
			)}
		</section>
	);
}
